import React, { useState, useEffect } from "react";
import {
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import useAxiosPrivate from "../utils/useAxiosPrivate";
import { useAuth } from "../utils/AuthProvider";
import { canManageDistributors } from "../utils/permissions";
import i18n from "../translationUtils/i18n";

function RegionGroupPanel({ group, cmSites, canManage, onChange }) {
  const axiosPrivate = useAxiosPrivate();
  const [distributors, setDistributors] = useState([]);
  const [addSiteId, setAddSiteId] = useState("");
  const [addDistributorId, setAddDistributorId] = useState("");

  const sitesInGroup = cmSites.filter((s) => s.regionId === group.regionId);
  const availableSites = cmSites.filter((s) => s.regionId !== group.regionId);
  const assignedDistributors = distributors.filter((d) => d.assigned);
  const availableDistributors = distributors.filter((d) => !d.assigned);

  const fetchDistributors = () => {
    axiosPrivate
      .get("/api/Site/regionDistributors/" + group.regionId)
      .then((r) => setDistributors(r.data))
      .catch(() => {});
  };

  useEffect(() => {
    fetchDistributors();
    // eslint-disable-next-line
  }, [group.regionId]);

  const setSiteRegion = (csId, regionId) => {
    axiosPrivate
      .post("/api/Site/setSiteRegion", { csId, regionId })
      .then(() => {
        setAddSiteId("");
        onChange();
      })
      .catch(() => alert(i18n.t("errorsavingregion")));
  };

  const setDistributorRegion = (userId, assigned) => {
    axiosPrivate
      .post("/api/Site/setDistributorRegion", {
        userId,
        regionId: group.regionId,
        assigned,
      })
      .then(() => {
        setAddDistributorId("");
        fetchDistributors();
        onChange();
      })
      .catch(() => alert(i18n.t("errorsavingregion")));
  };

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          {i18n.t("sites")}
        </Typography>
        {sitesInGroup.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {i18n.t("nositesinregion")}
          </Typography>
        )}
        <Stack spacing={0.5}>
          {sitesInGroup.map((s) => (
            <Stack
              key={s.csId}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="body2">
                {s.csName}
                {s.csState ? `, ${s.csState}` : ""}
              </Typography>
              {canManage && (
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => setSiteRegion(s.csId, null)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>
          ))}
        </Stack>
        {canManage && availableSites.length > 0 && (
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
            <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
              <InputLabel>{i18n.t("addsite")}</InputLabel>
              <Select
                label={i18n.t("addsite")}
                value={addSiteId}
                onChange={(e) => setAddSiteId(e.target.value)}
              >
                {availableSites.map((s) => (
                  <MenuItem key={s.csId} value={s.csId}>
                    {s.csName}
                    {s.regionName ? ` (${s.regionName})` : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              disabled={!addSiteId}
              onClick={() => setSiteRegion(addSiteId, group.regionId)}
            >
              {i18n.t("add")}
            </Button>
          </Stack>
        )}
      </Box>

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          {i18n.t("distributors")}
        </Typography>
        {assignedDistributors.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {i18n.t("nodistributorsinregion")}
          </Typography>
        )}
        <Stack spacing={0.5}>
          {assignedDistributors.map((d) => (
            <Stack
              key={d.userId}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="body2">{d.fullName || d.email}</Typography>
              {canManage && (
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => setDistributorRegion(d.userId, false)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>
          ))}
        </Stack>
        {canManage && availableDistributors.length > 0 && (
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
            <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
              <InputLabel>{i18n.t("adddistributor")}</InputLabel>
              <Select
                label={i18n.t("adddistributor")}
                value={addDistributorId}
                onChange={(e) => setAddDistributorId(e.target.value)}
              >
                {availableDistributors.map((d) => (
                  <MenuItem key={d.userId} value={d.userId}>
                    {d.fullName || d.email}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              disabled={!addDistributorId}
              onClick={() => setDistributorRegion(addDistributorId, true)}
            >
              {i18n.t("add")}
            </Button>
          </Stack>
        )}
      </Box>
    </Stack>
  );
}

export default function RegionGroups({ cmId }) {
  const { cookies } = useAuth();
  const axiosPrivate = useAxiosPrivate();
  const canManage = canManageDistributors(cookies.role);

  const [groups, setGroups] = useState([]);
  const [cmSites, setCmSites] = useState([]);
  const [newName, setNewName] = useState("");
  const [toDelete, setToDelete] = useState(null);

  const fetchGroups = (signal) =>
    axiosPrivate
      .get("/api/Site/regionGroups/" + cmId, { signal })
      .then((r) => setGroups(r.data))
      .catch(() => {});

  const fetchSites = (signal) =>
    axiosPrivate
      .get("/api/Site/cmSites/" + cmId, { signal })
      .then((r) => setCmSites(r.data))
      .catch(() => {});

  const refresh = () => {
    fetchGroups();
    fetchSites();
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchGroups(controller.signal);
    fetchSites(controller.signal);
    return () => controller.abort();
    // eslint-disable-next-line
  }, [cmId]);

  const handleCreate = () => {
    axiosPrivate
      .post("/api/Site/createRegionGroup", { cmId, groupName: newName.trim() })
      .then((r) => {
        alert(r.data);
        if (String(r.data).toLowerCase().includes("success")) {
          setNewName("");
          refresh();
        }
      })
      .catch(() => alert(i18n.t("errorsavingregion")));
  };

  const handleDelete = () => {
    axiosPrivate
      .post("/api/Site/deleteRegionGroup", { regionId: toDelete.regionId })
      .then(() => {
        setToDelete(null);
        refresh();
      })
      .catch(() => {
        alert(i18n.t("errorsavingregion"));
        setToDelete(null);
      });
  };

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom>
        {i18n.t("regiongroups")}
      </Typography>

      {canManage && (
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <TextField
            size="small"
            label={i18n.t("regiongroupname")}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            inputProps={{ maxLength: 30 }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            disabled={!newName.trim()}
            onClick={handleCreate}
          >
            {i18n.t("create")}
          </Button>
        </Stack>
      )}

      {groups.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {i18n.t("noregiongroups")}
        </Typography>
      )}

      {groups.map((g) => (
        <Accordion key={g.regionId} TransitionProps={{ unmountOnExit: true }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ flex: 1, pr: 1 }}
            >
              <Typography sx={{ flex: 1 }}>{g.groupName}</Typography>
              <Chip size="small" label={`${i18n.t("sites")}: ${g.siteCount}`} />
              <Chip
                size="small"
                label={`${i18n.t("distributors")}: ${g.distributorCount}`}
              />
              {canManage && (
                <Tooltip title={i18n.t("deleteregiongroup")}>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      setToDelete(g);
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <RegionGroupPanel
              group={g}
              cmSites={cmSites}
              canManage={canManage}
              onChange={refresh}
            />
          </AccordionDetails>
        </Accordion>
      ))}

      <Dialog open={toDelete !== null} onClose={() => setToDelete(null)}>
        <DialogTitle>{i18n.t("deleteregiongrouptitle")}</DialogTitle>
        <DialogContent>
          {toDelete && i18n.t("deleteregiongrouptip", { name: toDelete.groupName })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToDelete(null)} color="primary">
            {i18n.t("close")}
          </Button>
          <Button onClick={handleDelete} color="primary">
            {i18n.t("confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
